const BP = 980;

// ---------- London "today" + robust booleans ----------
const LDN_TZ = 'Europe/London';
const fmtLDN = (d, opts) => d.toLocaleString(undefined, { timeZone: LDN_TZ, ...opts });
const todayKeyLDN = (() => {
	const d = new Date();
	const y = fmtLDN(d, { year: 'numeric' });
	const m = fmtLDN(d, { month: '2-digit' });
	const dd = fmtLDN(d, { day: '2-digit' });
	return `${ y }-${ m }-${ dd }`;
})();

function boolish(v, def = true) {
	if (v === undefined || v === null) {
		return def;
	}
	if (typeof v === 'boolean') {
		return v;
	}
	if (typeof v === 'number') {
		return v !== 0;
	}
	const s = String(v).trim().toLowerCase();
	if (['', '0', 'false', 'no', 'off', 'null'].includes(s)) {
		return false;
	}
	if (['1', 'true', 'yes', 'on'].includes(s)) {
		return true;
	}
	return def;
}

// ---------- utils ----------
function money(v, cur = '£') {
	if (typeof v !== 'number' || !isFinite(v) || v <= 0) {
		return '';
	}
	const n = Math.round((v + Number.EPSILON) * 100) / 100;
	return cur + (Number.isInteger(n) ? String(n) : n.toFixed(2));
}
function timeLabel(iso) {
	const d = new Date(iso);
	const h = d.getHours(),
		m = String(d.getMinutes()).padStart(2, '0');
	const h12 = ((h + 11) % 12) + 1;
	return `${ h12 }${ m !== '00' ? '.' + m : '' }${ h >= 12 ? 'pm' : 'am' }`;
}
function slugify(s) {
	return String(s || '')
		.toLowerCase()
		.normalize('NFKD')
		.replace(/[^\w\s-]/g, '')
		.trim()
		.replace(/\s+/g, '-');
}
function sessionOf(timeStr) {
	const [hh, mm] = (timeStr || '').split(':').map((n) => parseInt(n, 10) || 0);
	return (hh > 17 || (hh === 17 && mm >= 30)) ? 'Evening' : 'Matinee';
}

// Maps each type slug to its CSS modifier class and accessible label text.
// Matches ACCESS_ICON_MAP in bigTicketCalendar.js and miniTicketCalendar.js.
const ACCESS_ICON_MAP = {
	ad: { mod: 'ad', label: 'Audio Described' },
	cap: { mod: 'cap', label: 'Captioned' },
	bsl: { mod: 'bsl', label: 'British Sign Language' },
	relaxed: { mod: 'relaxed', label: 'Relaxed' }
};

/**
 * Returns an HTML string of access performance type icons for a specific date
 * and time slot. Checks both the exact HH:MM time key and the 'any' key so
 * that access performances recorded without a specific time still appear on
 * every slot for that date.
 *
 * @param {Object} accessDates - The accessDates map from the calendar payload.
 * @param {string} date        - YYYY-MM-DD date string.
 * @param {string} time        - HH:MM 24-hour time string (from performance data).
 * @return {string}
 */
function accessIconsForSlot(accessDates, date, time) {
	const dateEntry = accessDates && accessDates[ date ];

	if (!dateEntry || typeof dateEntry !== 'object') {
		return '';
	}

	const types = new Set([
		...(dateEntry[ time ] || []),
		...(dateEntry.any || [])
	]);

	if (!types.size) {
		return '';
	}

	const icons = [...types].map((slug) => {
		const info = ACCESS_ICON_MAP[ slug ];

		if (!info) {
			return '';
		}

		return `<span class="farlo-ui-ticket-calendar__access-icon farlo-ui-ticket-calendar__access-icon--${ info.mod }" role="img" aria-label="${ info.label }"><span class="farlo-ui-ticket-calendar__access-icon-label">${ info.label }</span></span>`;
	}).filter(Boolean).join('');

	if (!icons) {
		return '';
	}

	return `<span class="farlo-ui-ticket-calendar__access-icons" aria-label="Access performance">${ icons }</span>`;
}
function getActiveFilters(preHeader, payload, access) {
	const act = { labels: new Set(), sessions: new Set(), offerFilter: null };
	if (!preHeader) {
		return act;
	}

	// label + session checkboxes
	preHeader.querySelectorAll('input[data-filter="label"]:checked')
		.forEach((i) => act.labels.add(String(i.value)));
	preHeader.querySelectorAll('input[data-filter="session"]:checked')
		.forEach((i) => act.sessions.add(String(i.value)));

	// Promotions chip → 'special' or 'exclusive'
	const promoChip = preHeader.querySelector('[data-chip="otd"]');

	const offerInput = preHeader.querySelector('input[data-filter="offer"]:checked');

	const chipOn = !!offerInput || (

		!!promoChip &&
		(
			promoChip.classList.contains('is-active') ||
			promoChip.getAttribute('aria-pressed') === 'true'
		)

	);

	if (chipOn) {
		const perfs = Array.isArray(payload?.performances) ? payload.performances : [];
		let hasOfferRow = false;
		let hasDiscount = false;
		for (const p of perfs) {
			if ((p.avail || 0) <= 0) {
				continue;
			}
			if (isCampaignRow(p, access)) {
				continue;
			}
			const pct = getSavingPercent(p);
			if (typeof pct === 'number' && pct > 0) {
				hasOfferRow = true;
				hasDiscount = true;
				break;
			}
			if (!isOltRow(p)) {
				hasOfferRow = true;
			}
		}
		if (hasOfferRow) {
			act.offerFilter = hasDiscount ? 'special' : 'exclusive';
		}
	}

	return act;
}
function filterPerfs(perfs, act, access) {
	return (Array.isArray(perfs) ? perfs : []).filter((p) => {
		if (typeof p.avail === 'number' && p.avail <= 0) {
			return false;
		}

		// Promotions chip has precedence over label filters
		if (act.offerFilter === 'special' && !isSpecialOfferRow(p, access)) {
			return false;
		}
		if (act.offerFilter === 'exclusive' && !isExclusivePriceRow(p, access)) {
			return false;
		}

		if (act.labels.size) {
			const labels = (p.labels || []).map(String);
			if (!labels.some((l) => act.labels.has(l))) {
				return false;
			}
		}
		if (act.sessions.size && !act.sessions.has(sessionOf(p.time))) {
			return false;
		}

		return true;
	});
}

// Build the predicate *after* we have payload + access
function makeIsOfferAllowed(payload, access) {
	const disableFutureDeals = boolish(
		payload?.showDisableFutureDeals ?? payload?.show_disable_future_deals,
		true
	);
	return (p) => {
		// Offer = non-OLT, non-campaign. Only clamp those.
		if (!disableFutureDeals) {
			return true;
		}
		if (isOltRow(p) || isCampaignRow(p, access)) {
			return true;
		}
		return String(p.date) === todayKeyLDN;
	};
}

function getSavingPercent(p) {
	if (typeof p.savePct === 'number' && p.savePct > 0) {
		return Math.round(p.savePct);
	}
	const direct = p.savePercent ?? p.savingPercent ?? p.discountPercent ?? p.discount_pct;
	if (typeof direct === 'number' && direct > 0) {
		return Math.round(direct);
	}
	const was = p.was ?? p.rrp ?? p.priceWas;
	const now = p.min;
	if (typeof was === 'number' && was > 0 && typeof now === 'number' && now > 0 && was > now) {
		return Math.round((1 - (now / was)) * 100);
	}
	return null;
}

function perfTimestamp(p) {
	const iso = p.iso || `${ p.date }T${ (p.time || '00:00') }:00`;
	const ms = Date.parse(iso);
	return Number.isNaN(ms) ? null : ms;
}
function isPastPerf(p) {
	const ms = perfTimestamp(p);
	const cutoff = Date.now() + (60 * 60 * 1000);
	return ms !== null && ms < cutoff;
}

function timeOnce(p, show) {
	const iso = p.iso || `${ p.date }T${ (p.time || '00:00') }:00`;
	return show
		? `<time class="farlo-ui-ticket-calendar__time">${ timeLabel(iso) }</time>`
		: '<span class="farlo-ui-ticket-calendar__time farlo-ui-ticket-calendar__time--placeholder" aria-hidden="true"></span>';
}

function isOltRow(p) {
	return String(p.source || '').toLowerCase() === 'olt' ||
         (p.labels || []).some((l) => /^olt$/i.test(String(l)));
}

function isCampaignRow(p, access) {
	const active = !!(access && access.brand && !access.finished);
	if (!active) {
		return false;
	}
	const labs = (p.labels || []).map((x) => String(x).toLowerCase());
	if (access.brand === 'kw') {
		return labs.some((l) => l.includes('kids'));
	}
	if (access.brand === 'nys' || access.brand === 'gilt') {
		return labs.some((l) => /new\s*year'?s?\s*sale/i.test(l));
	}
	return false;
}

function isKidsRow(p) {
	return (p.labels || []).some((l) => /kids/i.test(String(l || '')));
}

function isExclusivePriceRow(p, access) {
	// non-campaign, non-OLT, and no discount
	if (isCampaignRow(p, access) || isOltRow(p)) {
		return false;
	}
	const pct = getSavingPercent(p);
	return !(typeof pct === 'number' && pct > 0);
}

function parseIsoMs(iso) {
	const ms = Date.parse(iso || ''); return Number.isFinite(ms) ? ms : null;
}
// Check for campaign cookie client-side (bypasses page cache)
function hasCampaignCookie() {
	return document.cookie.includes('farlo_campaign_access');
}
function canBookNowFromAccess(access) {
	if (!access || access.finished) {
		return false;
	}
	const revealMs = parseIsoMs(access.revealIso);
	const prioMs = parseIsoMs(access.priorityIso);
	// Check cookie client-side instead of cached eligible flag
	const openAt = (hasCampaignCookie() && prioMs !== null) ? prioMs : revealMs;
	return openAt != null && Date.now() >= openAt;
}
function onSaleMsgFromAccess(access) {
	const revealMs = parseIsoMs(access.revealIso);
	const prioMs = parseIsoMs(access.priorityIso);
	// Check cookie client-side instead of cached eligible flag
	const tMs = (hasCampaignCookie() && prioMs !== null) ? prioMs : revealMs;
	if (tMs === null) {
		return 'On sale soon';
	}

	const t = new Date(tMs);
	const sameDay =
    fmtLDN(t, { year: 'numeric', month: '2-digit', day: '2-digit' }) ===
    fmtLDN(new Date(), { year: 'numeric', month: '2-digit', day: '2-digit' });

	const timeStr = fmtLDN(t, { hour: 'numeric', minute: '2-digit', hour12: true })
		.replace(':00', '')
		.replace(' ', '')
		.toLowerCase();

	if (sameDay) {
		return `On sale at ${ timeStr }`;
	}
	const dateStr = fmtLDN(t, { day: 'numeric', month: 'long' });
	return `On sale on ${ dateStr } at ${ timeStr }`;
}
function isSpecialOfferRow(p, access) {
	const pct = getSavingPercent(p);
	return typeof pct === 'number' && pct > 0 && !isCampaignRow(p, access);
}

function bestSavePct(rows) {
	let best = null;
	(rows || []).forEach((r) => {
		const pct = getSavingPercent(r);
		if (typeof pct === 'number' && pct > 0) {
			best = best == null ? pct : Math.max(best, pct);
		}
	});
	return best;
}

function buildListHTML(payload) {
	const currency = payload.currency || '£';
	const access = payload.campaignAccess || {};
	const specialOfferNote = (payload.specialOfferNote || '').toString().trim();
	const exclusiveOfferNote = (payload.exclusiveOfferNote || '').toString().trim();
	const hideDiscountMessaging = boolish(
		payload?.hideDiscountMessaging ?? payload?.hide_discount_messaging,
		false
	);
	const otherSeatsBadge = `<span class="farlo-ui-ticket-calendar__badge farlo-ui-ticket-calendar__badge--other-seats">View other available seats</span>`;
	const showCampaignUI = !!(access.brand && !access.finished);

	/*
	 * Multi-part show price multiplier.
	 *
	 * Multi-part productions require a separate ticket purchase for each part,
	 * so every displayed price is multiplied by the number of parts.
	 * Kids Week prices are further doubled (one adult + one child per part).
	 *
	 * partsCount is supplied by PHP (olt_calendar_payload_for_show) from the
	 * show_multiple_parts and show_multiple_parts_amount ACF fields.
	 */
	const partsCount = (Number.isInteger(payload.partsCount) && payload.partsCount > 0) ? payload.partsCount : 1;

	// Access performance dates keyed by YYYY-MM-DD, sourced from the PHP calendar payload.
	// Inner keys are HH:MM time strings (or 'any') mapping to arrays of type slugs.
	const accessDates = (payload.accessDates && typeof payload.accessDates === 'object')
		? payload.accessDates
		: {};

	// Build the offer predicate *now* that we have payload + access
	const isOfferAllowed = makeIsOfferAllowed(payload, access);

	const perfs = Array.isArray(payload.performances) ? payload.performances.slice() : [];
	if (!perfs.length) {
		return '<p class="farlo-ui-ticket-calendar__empty">No performances available.</p>';
	}

	const upcoming = perfs.filter((p) => !isPastPerf(p));
	if (!upcoming.length) {
		return '<p class="farlo-ui-ticket-calendar__empty">No performances available.</p>';
	}

	// sort by date then time
	upcoming.sort((a, b) =>
		(a.date || '').localeCompare(b.date || '') ||
    (a.time || '').localeCompare(b.time || '')
	);

	// group by day
	const groups = new Map();
	upcoming.forEach((p) => {
		const k = p.date;
		(groups.get(k) || groups.set(k, []).get(k)).push(p);
	});

	const _esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ c ]));

	const renderSection = (title, rows, { mod = '', showPct = false, note = '', showOtherSeatsBadge = false, cheapest = 0 } = {}) => {
		if (!rows.length) {
			return '';
		}
		const bestPct = showPct ? bestSavePct(rows) : null;
		const sectionIsOffer = mod === 'farlo-ui-ticket-calendar__section--offer';

		// group inside the section by time
		const byTime = rows.reduce((acc, p) => {
			(acc[ p.time ] ||= []).push(p);
			return acc;
		}, {});

		const body = Object.values(byTime).map((group) =>
			group.map((p, i) => {
				const isCamp = isCampaignRow(p, access);
				const locked = isCamp && !canBookNowFromAccess(access);

				const pMin = (p.min !== null && p.min !== undefined) ? Number(p.min) : null;
				// Apply the parts multiplier. Kids Week adds an additional x2 for the
				// mandatory adult + child pair.
				const displayMin = pMin !== null ? pMin * partsCount * (isKidsRow(p) ? 2 : 1) : null;
				const price = (displayMin !== null && displayMin > 0) ? money(displayMin, currency) : '';
				const href = p.bookUrl || p.url || '#';
				const isCheapest = (pMin !== null && cheapest > 0 && Math.abs(pMin - cheapest) < 0.01);
				const disc = p.savePct ?? p.discount_pct ?? p.discountPercent ?? p.savingPercent ?? 0;
				const dataAttrs = `data-event="click_price" data-id="${ payload.showId || '' }" data-name="${ _esc(payload.showName || '') }" data-offer="${ sectionIsOffer }" data-cheapest="${ isCheapest }" data-offer-perc="${ sectionIsOffer ? (parseInt(disc, 10) || 0) : 0 }" data-price="${ pMin !== null ? pMin.toFixed(2) : '' }" data-source="${ _esc(p.source || '') }"`;

				const campaignPrefix = isKidsRow(p) ? 'Adult + Child' : 'Exclusive prices';

				const rightSide = locked
					? `<div class="farlo-ui-ticket-calendar__from farlo-ui-ticket-calendar__from--locked">${ onSaleMsgFromAccess(access) }</div>`
					: `${ price ? `<div class="farlo-ui-ticket-calendar__from"><span>${ isCampaignRow(p, access) ? campaignPrefix + ' ' : '' }from</span> <strong>${ price }</strong></div>` : '' }
				<a class="farlo-ui-ticket-calendar__book button button--book" href="${ href }" ${ dataAttrs }>Book Tickets</a>`;

				return `
      <div class="farlo-ui-ticket-calendar__option-row${ mod === 'farlo-ui-ticket-calendar__section--offer' ? ' farlo-ui-ticket-calendar__option-row--offer' : '' }">
        <div class="farlo-ui-ticket-calendar__option-left">
          <span class="farlo-ui-ticket-calendar__slot-time-group">
            ${ timeOnce(p, i === 0) }${ i === 0 ? accessIconsForSlot(accessDates, p.date, p.time) : '' }
          </span>
          ${ (showPct && bestPct && !hideDiscountMessaging) ? `<span class="farlo-ui-ticket-calendar__badge farlo-ui-ticket-calendar__badge--save">up to ${ bestPct }% off</span>` : '' }
          ${ (!showPct && showOtherSeatsBadge && i === 0) ? otherSeatsBadge : '' }
        </div>
        <div class="farlo-ui-ticket-calendar__option-right">
          ${ rightSide }
        </div>
      </div>`;
			}).join('')
		).join('');

		return `
      <div class="farlo-ui-ticket-calendar__section ${ mod }">
        ${ (title && String(title).trim())
		? `<h4 class="farlo-ui-ticket-calendar__section-title">${ title }</h4>`
		: '' }
        ${ body }
        ${ note ? `<p class="farlo-ui-ticket-calendar__offer-note">${ note }</p>` : '' }
      </div>`;
	};

	const out = [];
	for (const [day, rows] of groups) {
		const hdr = new Date(`${ day }T12:00:00`);
		const header = hdr.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

		const oltRows = rows.filter(isOltRow);
		const nonOltRows = rows.filter((p) => !isOltRow(p) && !isCampaignRow(p, access));

		// Clamp only offer-like rows (not full OLT)
		const nonOltOffers = nonOltRows.filter(isOfferAllowed);

		// Does OLT have any discounts today?
		const oltHasDiscounts = oltRows.some((p) => {
			const pct = getSavingPercent(p);
			return typeof pct === 'number' && pct > 0;
		});

		let displayFullRows;
		let offerRowsAll;

		if (oltHasDiscounts) {
			const discountedOlt = oltRows.filter((p) => {
				const pct = getSavingPercent(p);
				return typeof pct === 'number' && pct > 0;
			});
			const fullOlt = oltRows.filter((p) => {
				const pct = getSavingPercent(p);
				return !(typeof pct === 'number' && pct > 0);
			});
			const oltTimes = new Set(discountedOlt.map((p) => p.time));
			const dedupNonOlt = nonOltOffers.filter((p) => !oltTimes.has(p.time));
			offerRowsAll = [...discountedOlt, ...dedupNonOlt];
			displayFullRows = fullOlt;
		} else {
			offerRowsAll = nonOltOffers;
			displayFullRows = oltRows;
		}

		// Title resolves from whether any “offer” row actually has a discount
		const offerBestPct = bestSavePct(offerRowsAll);
		const offerTitle = hideDiscountMessaging
			? ''
			: ((offerRowsAll.length && (!offerBestPct || offerBestPct <= 0))
				? 'Exclusive Price'
				: 'Special Offer');

		const offerNote = hideDiscountMessaging ? '' : (offerTitle === 'Exclusive Price' ? exclusiveOfferNote : specialOfferNote);
		const kwRows = showCampaignUI ? rows.filter((r) => (r.labels || []).some((l) => /kids/i.test(String(l)))) : [];
		const nysRows = showCampaignUI
			? rows.filter((r) => (r.labels || []).some((l) => /new\s*year'?s?\s*sale/i.test(String(l))))
			: [];

		const _allMins = [...offerRowsAll, ...displayFullRows].map((p) => Number(p.min)).filter((v) => v > 0);
		const _cheapest = _allMins.length ? Math.min(..._allMins) : 0;

		const sections =
		(showCampaignUI ? renderSection('Kids Week', kwRows, { mod: 'farlo-ui-ticket-calendar__section--kw', showPct: false, cheapest: _cheapest }) : '') +
		(showCampaignUI ? renderSection('New Year Sale', nysRows, { mod: 'farlo-ui-ticket-calendar__section--nys', showPct: false, cheapest: _cheapest }) : '') +
		(offerRowsAll.length ? renderSection(offerTitle, offerRowsAll, { mod: 'farlo-ui-ticket-calendar__section--offer', showPct: Boolean(offerBestPct) && !hideDiscountMessaging, note: offerNote, cheapest: _cheapest }) : '') +
		renderSection('', displayFullRows, { mod: 'farlo-ui-ticket-calendar__section--full', showOtherSeatsBadge: true, cheapest: _cheapest });
		out.push(`
      <li class="farlo-ui-ticket-calendar__list-group">
        <h3 class="farlo-ui-ticket-calendar__list-date">${ header }</h3>
        <div class="farlo-ui-ticket-calendar__list-group-body">
          ${ sections || '<p class="farlo-ui-ticket-calendar__empty">No performances matching your filters.</p>' }
        </div>
      </li>`);
	}

	return `<div class="farlo-ui-ticket-calendar__list"><ul class="farlo-ui-ticket-calendar__list-body">${ out.join('') }</ul></div>`;
}

export function initCalendarMobileToggle() {
	const startWhenReady = () => {
		const mount = document.getElementById('calendar-toggle-container');
		const miniRoot = document.querySelector('[farlo-ui="calendar-mini"], [farlo-ui="calendar-mini-alternative"]');
		if (!mount || !miniRoot) {
			return false;
		}
		boot(mount, miniRoot);
		return true;
	};

	if (startWhenReady()) {
		return;
	}

	const finder = new MutationObserver(() => {
		if (startWhenReady()) {
			finder.disconnect();
		}
	});
	finder.observe(document.documentElement, { childList: true, subtree: true });
}

function boot(mount, miniRoot) {
	// placeholders & references
	const miniOriginalParent = miniRoot.parentNode;
	const miniOriginalNext = miniRoot.nextSibling;
	const miniPlaceholder = document.createComment('mini-calendar-placeholder');
	miniOriginalParent.insertBefore(miniPlaceholder, miniOriginalNext);

	// True when the show is using the alternative ticketing URL booking link
	// rather than a synced calendar. This block has no dates to switch
	// between, so the Dates/List toggle scaffold built further down is
	// skipped in favour of relocating it into the mount as-is.
	const isAlternativeMini = miniRoot.getAttribute('farlo-ui') === 'calendar-mini-alternative';

	let bigRoot =
    document.querySelector('[farlo-ui="calendar-big"], [farlo-ui="calendar-big-alternative"]') ||
    document.querySelector('.farlo-ui-ticket-calendar--big');
	const bigOriginalParent = bigRoot ? bigRoot.parentNode : null;
	const bigOriginalNext = bigRoot ? bigRoot.nextSibling : null;

	const miniWrapper = miniRoot.closest('.farlo-ui-ticket-calendar__wrapper') || miniOriginalParent;
	const preHeader =
    (miniWrapper && miniWrapper.querySelector('.farlo-ui-ticket-calendar__pre-header')) ||
    document.querySelector('.farlo-ui-ticket-calendar__pre-header');
	const preHeaderOriginalParent = preHeader ? preHeader.parentNode : null;
	const preHeaderOriginalNext = preHeader ? preHeader.nextSibling : null;

	// scaffold
	let nav, miniPanel, listPanel,
		mobileScaffoldBuilt = false;

	const rebuildListFromMini = () => {
		if (!listPanel) {
			return;
		}
		let payload;
		try {
			payload = JSON.parse(miniRoot.getAttribute('data-cal') || '{}') || {};
		} catch {
			payload = {};
		}
		const act = getActiveFilters(preHeader, payload, payload.campaignAccess || {});
		const filtered = filterPerfs(payload.performances, act, payload.campaignAccess || {});
		const model = { ...payload, performances: filtered };
		listPanel.innerHTML = buildListHTML(model);
	};

	const miniAttrObserver = new MutationObserver(() => {
		if (window.innerWidth < BP && listPanel && !listPanel.hasAttribute('inert')) {
			rebuildListFromMini();
		}
	});

	const ensureMobileScaffold = () => {
		if (isAlternativeMini) {
			if (mobileScaffoldBuilt) {
				return;
			}
			if (preHeader) {
				mount.replaceChildren(preHeader, miniRoot);
			} else {
				mount.replaceChildren(miniRoot);
			}
			mobileScaffoldBuilt = true;
			return;
		}

		if (nav) {
			return;
		}

		nav = document.createElement('nav');
		nav.className = 'cal-toggle';
		nav.setAttribute('role', 'tablist');
		nav.setAttribute('aria-label', 'Dates view');
		nav.innerHTML = `
      <button class="cal-toggle__btn" role="tab" aria-selected="true"  aria-controls="cal-mini" data-view="calendar">Dates</button>
      <button class="cal-toggle__btn" role="tab" aria-selected="false" aria-controls="cal-list" data-view="list">List</button>
    `;

		miniPanel = document.createElement('div');
		miniPanel.id = 'cal-mini';
		miniPanel.className = 'calendar-toggle__mini';
		miniPanel.setAttribute('role', 'tabpanel');

		listPanel = document.createElement('div');
		listPanel.id = 'cal-list';
		listPanel.className = 'calendar-toggle__list';
		listPanel.setAttribute('role', 'tabpanel');
		listPanel.setAttribute('inert', '');

		if (preHeader) {
			mount.replaceChildren(preHeader, nav, miniPanel, listPanel);
		} else {
			mount.replaceChildren(nav, miniPanel, listPanel);
		}

		nav.querySelectorAll('.cal-toggle__btn').forEach((btn) => {
			btn.addEventListener('click', (e) => {
				e.preventDefault();
				setState(btn.dataset.view);
				history.replaceState(null, '', btn.dataset.view === 'calendar' ? '#calendar' : '#list');
			});
		});

		if (preHeader) {
			preHeader.addEventListener('change', () => setTimeout(rebuildListFromMini, 0));
			preHeader.addEventListener('click', (ev) => {
				const t = ev.target;
				if (t && (t.matches('button') || t.closest('button'))) {
					setTimeout(rebuildListFromMini, 0);
				}
			});
			const clearBtn = preHeader.querySelector('.farlo-ui-ticket-calendar__filter-clear');
			if (clearBtn) {
				clearBtn.addEventListener('click', () => setTimeout(rebuildListFromMini, 0));
			}
		}

		miniAttrObserver.observe(miniRoot, { attributes: true, attributeFilter: ['data-cal'] });
	};

	const destroyMobileScaffold = () => {
		if (!nav && !miniPanel && !listPanel && !mobileScaffoldBuilt) {
			return;
		}
		if (nav) {
			nav.querySelectorAll('.cal-toggle__btn').forEach((btn) => {
				btn.replaceWith(btn.cloneNode(true));
			});
		}
		miniAttrObserver.disconnect();
		if (preHeader && preHeaderOriginalParent) {
			preHeaderOriginalParent.insertBefore(preHeader, preHeaderOriginalNext);
		}
		mount.replaceChildren();
		nav = miniPanel = listPanel = null;
		mobileScaffoldBuilt = false;
	};

	const setState = (view) => {
		const isCal = view === 'calendar';
		nav.querySelectorAll('.cal-toggle__btn')
			.forEach((b) => b.setAttribute('aria-selected', String(b.dataset.view === view)));
		if (isCal) {
			miniPanel.removeAttribute('inert');
			listPanel.setAttribute('inert', '');
		} else {
			miniPanel.setAttribute('inert', '');
			listPanel.removeAttribute('inert');
		}
		sessionStorage.setItem('olt.mobileShowView', view);
		if (!isCal) {
			rebuildListFromMini();
		}
	};

	const detachBig = () => {
		const currentBig =
      document.querySelector('[farlo-ui="calendar-big"], [farlo-ui="calendar-big-alternative"]') ||
      document.querySelector('.farlo-ui-ticket-calendar--big');
		if (currentBig && currentBig.parentNode) {
			if (!bigRoot) {
				bigRoot = currentBig;
			}
			currentBig.parentNode.removeChild(currentBig);
		}
	};
	const restoreBig = () => {
		if (!bigRoot || bigRoot.parentNode) {
			return;
		}
		if (bigOriginalParent) {
			bigOriginalParent.insertBefore(bigRoot, bigOriginalNext);
		}
	};

	const hideMiniOriginalWrapper = (hide) => {
		const originalWrapper = miniOriginalParent.closest('[data-mini-calendar-original]') || miniOriginalParent;
		if (originalWrapper) {
			originalWrapper.style.display = hide ? 'none' : '';
		}
	};
	const moveMiniToMobile = () => {
		ensureMobileScaffold();
		if (!isAlternativeMini && miniRoot.parentNode !== miniPanel) {
			miniPanel.appendChild(miniRoot);
		}
		hideMiniOriginalWrapper(true);
	};
	const restoreMiniToDesktop = () => {
		if (miniRoot.parentNode !== miniOriginalParent) {
			miniOriginalParent.insertBefore(miniRoot, miniPlaceholder.nextSibling);
		}
		hideMiniOriginalWrapper(false);
		if (miniPanel && listPanel) {
			miniPanel.setAttribute('inert', '');
			listPanel.setAttribute('inert', '');
		}
	};

	// responsive apply
	const apply = () => {
		const isMobile = window.innerWidth < BP;
		if (isMobile) {
			moveMiniToMobile();
			detachBig();
			if (!isAlternativeMini) {
				setState(sessionStorage.getItem('olt.mobileShowView') || 'calendar');
			}
		} else {
			restoreMiniToDesktop();
			restoreBig();
			destroyMobileScaffold();
		}
	};

	let rAF = null;
	const onResize = () => {
		if (rAF) {
			cancelAnimationFrame(rAF);
		}
		rAF = requestAnimationFrame(apply);
	};

	// late re-renders
	const mo = new MutationObserver(() => {
		const isMobile = window.innerWidth < BP;
		if (isMobile) {
			detachBig();
			const relocated = isAlternativeMini ? miniRoot.parentNode === mount : miniRoot.parentNode === miniPanel;
			if (miniRoot && !relocated) {
				moveMiniToMobile();
			}
		} else {
			restoreBig();
			if (miniRoot && miniRoot.parentNode !== miniOriginalParent) {
				restoreMiniToDesktop();
			}
			destroyMobileScaffold();
		}
	});

	// init
	apply();
	window.addEventListener('resize', onResize);
	mo.observe(document.body, { childList: true, subtree: true });

	// deep links
	if (!isAlternativeMini) {
		if (location.hash === '#list') {
			ensureMobileScaffold(); setState('list');
		}
		if (location.hash === '#calendar') {
			ensureMobileScaffold(); setState('calendar');
		}
	}
}
